import type { JSX } from 'react';
import type { StrategyResult } from '@/types';
import { formatCurrency } from '@/utils/format';
import { BreakdownChart } from './BreakdownChart';
import { Button } from './ui';

interface Props {
  strategy: StrategyResult;
  onSelect?: () => void;
  isSelected?: boolean;
  title?: string;
}

/**
 * Shared strategy card component for both PAYE and Contractor strategies.
 * Displays a breakdown chart with tax, NI, pension, and charity data.
 */
export function StrategyCard({ strategy, onSelect, isSelected = false, title }: Props): JSX.Element {
  // Calculate total benefits (taxable benefits + free childcare currently secured - child benefit charge)
  const securedFreeChildcare =
    strategy.free_childcare.type === 'eligible' || strategy.free_childcare.type === 'reduced'
      ? strategy.free_childcare.value
      : 0;
  const totalBenefits = strategy.taxable_benefits + securedFreeChildcare - strategy.child_benefit_repayment;

  const taxLabel = strategy.contractor_breakdown
    ? 'Total Tax (corp + income + dividend)'
    : 'Income Tax';

  // Build breakdown data for chart
  const breakdown = [
    { name: 'Cash in Hand', value: strategy.cash_in_hand },
    ...(totalBenefits > 0 ? [{ name: 'Benefits', value: totalBenefits }] : []),
    { name: taxLabel, value: strategy.total_tax },
    { name: 'National Insurance', value: strategy.national_insurance },
    { name: 'Charity with Gift-Aid', value: strategy.recommended_charity },
    { name: 'Pension inc. HMRC top-up', value: strategy.recommended_pension },
  ];

  // Build warnings array
  const warnings: string[] = [];

  // Free childcare warnings (the "lost" variant means no universal hours kept)
  if (strategy.free_childcare.type === 'lost') {
    const lost = strategy.free_childcare.lost;
    warnings.push(
      lost > 0
        ? `You'll lose free childcare eligibility (worth ~${formatCurrency(lost)}/year)`
        : "You'll lose free childcare eligibility"
    );
  }

  // Tax-free childcare warnings
  if (strategy.tax_free_childcare.type === 'lost' && strategy.tax_free_childcare.lost > 0) {
    warnings.push(
      `You'll lose Tax-Free Childcare (worth up to ${formatCurrency(strategy.tax_free_childcare.lost)}/year)`
    );
  }

  // Child benefit repayment
  if (strategy.child_benefit_repayment > 0) {
    warnings.push(`Child Benefit repayment: ${formatCurrency(strategy.child_benefit_repayment)}`);
  }

  // Pension suggestions (if there's a suggested incremental contribution)
  if (strategy.suggested_pension > 0) {
    warnings.push(`Suggested pension: ${formatCurrency(strategy.suggested_pension)}`);
  }

  const headerSlot = onSelect ? (
    <Button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      variant={isSelected ? "primary" : "secondary"}
      fullWidth
      className={`py-3 text-sm font-semibold border-b rounded-none ${
        isSelected
          ? "border-primary"
          : "border-gray-200 hover:bg-gray-100"
      }`}
    >
      {isSelected ? "Preferred strategy" : "Select strategy"}
    </Button>
  ) : undefined;

  return (
    <BreakdownChart
      data={breakdown}
      title={title ?? strategy.name ?? 'Strategy'}
      description={strategy.description}
      warnings={warnings}
      headerSlot={headerSlot}
      highlight={isSelected}
    />
  );
}
